"use client";

import clsx from "clsx";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { cn } from "@/lib/utils";
import { useOutOfBoundsHitToggle, useWindowResizeSizing } from "@/lib/hooks";
import { formatServiceDisplayTitle } from "@/lib/utils/service-title";
import type { Basket, BasketItem, Service } from "@/types";
import { useGetCustomServicesByIdsQuery, useGetServicesByIdsQuery } from "@/features/services/serviceApiInjection";

import { BasketDropdownSkeleton } from "@/app/basket/components/BasketLoadingSkeleton";
import { Button } from "@/components/ui/button";
import TrackedCtaLink from "@/components/analytics/TrackedCtaLink";

import CartIcon from "@/components/icons/CartIcon";

const NAV_DROPDOWN_OPEN_EVENT = "techxeed:nav-dropdown-open";
const NAV_DROPDOWN_ID = "basket";

export default function NavBasketButton() {
	const wrapperRef = useRef<HTMLDivElement | null>(null);
	const [showDropdown, setDropdownVisibility] = useState(false);
	const [mounted, setMounted] = useState(false);
	const basket = useSelector((state: { basket: Basket }) => state.basket);

	const basketEntires = useMemo(() => Object.entries(basket.items), [basket.items]);

	useOutOfBoundsHitToggle(wrapperRef, [showDropdown, setDropdownVisibility]);

	useEffect(() => {
		// Mark as mounted after hydration to prevent hydration mismatches
		setMounted(true);
	}, []);

	useEffect(() => {
		const closeForOtherDropdown = (event: Event) => {
			if ((event as CustomEvent<string>).detail !== NAV_DROPDOWN_ID) {
				setDropdownVisibility(false);
			}
		};

		window.addEventListener(NAV_DROPDOWN_OPEN_EVENT, closeForOtherDropdown);
		return () => window.removeEventListener(NAV_DROPDOWN_OPEN_EVENT, closeForOtherDropdown);
	}, []);

	return (
		<div ref={wrapperRef} className="group sm:relative">
			<button
				aria-haspopup="menu"
				aria-controls="basket-dropdown"
				aria-expanded={showDropdown}
				aria-label="Basket"
				className="relative z-[70] flex min-h-[44px] min-w-[44px] items-center justify-center md:size-10"
				onClick={() => {
					const nextOpen = !showDropdown;
					setDropdownVisibility(nextOpen);
					if (nextOpen) {
						window.dispatchEvent(new CustomEvent(NAV_DROPDOWN_OPEN_EVENT, { detail: NAV_DROPDOWN_ID }));
					}
				}}
			>
				<CartIcon
					className="size-5 text-foreground md:size-6"
					aria-hidden
				/>

				{/* Only show badge after hydration to prevent hydration mismatches - positioned relative to parent div */}
				{mounted && basketEntires.length > 0 && (
					<div 
						id="basket-badge-count"
						className="pointer-events-none absolute right-0.5 top-0 z-[80] inline-flex size-3.5 items-center justify-center rounded-full text-[10px] font-bold text-white shadow-md md:right-1 md:top-0.5 md:size-4 md:text-xxsm"
						style={{ 
							backgroundColor: 'rgb(63, 224, 208)',
							background: 'rgb(63, 224, 208)',
							opacity: 1,
						}}
					>
						<span style={{ color: '#FFFFFF', fontWeight: 'bold' }}>{basketEntires.length}</span>
					</div>
				)}
			</button>
			{showDropdown && mounted && (
				<div
					id="basket-dropdown"
					className="fixed left-1/2 top-[calc(90px+1.5rem)] z-[60] w-[calc(100vw-2rem)] max-w-sm -translate-x-1/2 md:absolute md:left-auto md:right-0 md:top-5 md:w-auto md:max-w-none md:translate-x-0"
					onClick={() => setDropdownVisibility(false)}
				>
					<div aria-hidden className="hidden md:block h-12" />
					{basketEntires.length > 0 ? (
						<BasketDropdown items={basketEntires} />
					) : (
						<BasketDropdownEmpty />
					)}
				</div>
			)}
		</div>
	);
}

function BasketDropdownEmpty() {
	return (
		<div 
			className="dropdown-glassmorphism flex h-32 w-full flex-col items-center justify-center gap-1 rounded-2xl sm:w-96"
			style={{
				border: '1px solid var(--glass-border)',
				boxShadow: 'var(--glass-shadow)',
				background: 'var(--glass-background)',
			}}
		>
			<CartIcon className="size-8 text-foreground" aria-hidden />
			<span className="text-base font-medium">Your basket is empty</span>
		</div>
	);
}

type BasketDropdownProps = { 
	items: [string, BasketItem][];
};
function BasketDropdown({ items }: BasketDropdownProps) {
	const [mounted, setMounted] = useState(false);
	const dropdownMaxHeight = useWindowResizeSizing(0, -100);

	useEffect(() => {
		setMounted(true);
	}, []);

	const ids = useMemo(() => items.map(([x, { serviceId }]) => serviceId).join(","), [items]);
	const { data, isLoading } = useGetServicesByIdsQuery(ids, {
		skip: !ids || ids === "" || !mounted,
		refetchOnMountOrArgChange: false,
		refetchOnFocus: false,
		refetchOnReconnect: false,
	});
	const { data: customData, isLoading: isCustomLoading } = useGetCustomServicesByIdsQuery(ids, {
		skip: !ids || ids === "" || !mounted,
		refetchOnMountOrArgChange: false,
		refetchOnFocus: false,
		refetchOnReconnect: false,
	});

	const keyedData: Record<string, Service> = useMemo(() => {
		if (data === undefined || customData === undefined) return {};

		return data.docs.concat(customData.docs).reduceRight(
			(prev: Record<string, Service>, x: Service) => {
				prev[x._id] = x;
				return prev;
			},
			{} as Record<string, Service>,
		);
	}, [data, customData]);

	// Use consistent className on initial render to prevent hydration mismatch
	// Always show loading state until mounted
	if (!mounted || isLoading || !data || isCustomLoading || !customData) {
		return <BasketDropdownSkeleton />;
	}

	const empty = data?.docs.length === 0 && customData?.docs.length === 0;
	const baseClass = empty 
		? "dropdown-glassmorphism flex h-32 w-full flex-col items-center justify-center rounded-2xl sm:w-96"
		: "dropdown-glassmorphism flex flex-col rounded-2xl w-full sm:w-96";

	if (empty) {
		return <BasketDropdownEmpty />;
	}

	return (
		<div
			className={baseClass}
			style={{ 
				maxHeight: `${dropdownMaxHeight.current}px`,
				border: '1px solid var(--glass-border)',
				boxShadow: 'var(--glass-shadow)',
				background: 'rgba(255, 255, 255, 0.25)',
			}}
			// Note(Curstantine): stop bubbling click events on this div to the parent to prevent the dropdown from closing
			// unless of course if it's an Link element.
			onClick={(e) => {
				const { tagName } = e.target as Element;
				if (tagName !== "A") e.stopPropagation();
			}}
		>
			{/* Fixed Header */}
			<div className="flex h-12 shrink-0 items-center justify-center gap-4 px-6 pt-4">
				<CartIcon className="size-7 text-foreground" aria-hidden />
				<span className="text-md font-medium">Basket</span>
			</div>

			{/* Scrollable Items List */}
			<div 
				className="flex-1 overflow-y-auto"
				style={{ 
					maxHeight: `${dropdownMaxHeight.current - 120}px`, // Subtract header and footer height
				}}
			>
			{items.map(([basketId, { serviceId }]) => {
				const { title, description } = keyedData[serviceId];
				const displayTitle = formatServiceDisplayTitle(title);

				return (
					<div
						key={basketId}
						className="flex select-none flex-col border-y border-secondary/[0.02] px-6 py-2"
					>
						<div className="text-sm font-medium leading-tight">{displayTitle}</div>
						<div className="truncate text-xs">{description}</div>
					</div>
				);
			})}
			</div>

			{/* Fixed Footer */}
			<div 
				className="flex w-full shrink-0 justify-center border-t border-secondary/[0.02] bg-background px-8 py-4"
				style={{
					backdropFilter: 'none',
					WebkitBackdropFilter: 'none',
				}}
			>
				<Button
					id="basket-go-to-button"
					variant="accent"
					className="shadow-md"
					style={{
						backgroundColor: "rgb(63, 224, 208)",
						background: "rgb(63, 224, 208)",
						color: "#FFFFFF",
						opacity: 1,
					}}
					asChild
				>
					<TrackedCtaLink
						href="/basket"
						ctaId="nav_go_to_basket"
						ctaName="Go to Basket"
						ctaLocation="nav_basket_dropdown"
					>
						Go to Basket
					</TrackedCtaLink>
				</Button>
			</div>
		</div>
	);
}
